import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {RootState} from '../../../redux/store.type';
import {getDateFormat, getStartOfDay} from '../../../library/dates';
import setGlobalState from '../../../redux/setGlobalState';
import {TTimetableItem} from '../../../redux/timetable/timetable.type';
import {getLectureTime} from './useTimeGroups';

const ONE_DAY_S = 86_400;

const getDayKey = (timestamp: number) => getDateFormat(timestamp, 'YYYY-M-D');

const formatDayLabel = (timestamp: number, lang: string, dayPrefix: string) => {
    const dayName = dayjs.unix(timestamp).locale(lang).format('dddd');
    return `${dayPrefix} ${dayName} - ${getDateFormat(timestamp, 'D.M.YY')}`;
};

export interface TConferenceDay {
    key: string;
    label: string;
    timestamp: number;
    count: number;
}

export function useTimetableDays() {
    const {i18n, t} = useTranslation('timetable');
    const timeStart = useSelector((state: RootState) => state.event.timeStart);
    const timeEnd = useSelector((state: RootState) => state.event.timeEnd);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const currentDayIndex = useSelector((state: RootState) => state.timetable.currentDayIndex);
    const setCurrentDayIndex = (i: number) => setGlobalState('timetable.currentDayIndex', i);
    const dayPrefix = t('day');

    const daySkeleton = useMemo(() => {
        if(!timeStart || !timeEnd) return [];
        const days: {key: string; label: string; timestamp: number}[] = [];
        let dayTs = getStartOfDay(timeStart);
        const lastDay = getStartOfDay(timeEnd);
        while(dayTs <= lastDay) {
            days.push({
                key: getDayKey(dayTs),
                label: formatDayLabel(dayTs, i18n.language, dayPrefix),
                timestamp: dayTs,
            });
            dayTs += ONE_DAY_S;
        }
        return days;
    }, [timeStart, timeEnd, i18n.language, dayPrefix]);

    const conferenceDays = useMemo<TConferenceDay[]>(() => daySkeleton.map(day => ({
        ...day,
        // Count only lectures that actually fall on this day — the bucket can hold
        // entries fetched/keyed under the wrong day, which would inflate the badge.
        // Use the same day-match as the list (useTimeGroups) so they never disagree.
        count: (lecturesByDay[day.timestamp] ?? []).filter(
            (l: TTimetableItem) => !l.isBreak && getDayKey(getLectureTime(l, slots)) === day.key,
        ).length,
    })), [daySkeleton, lecturesByDay, slots]);

    const currentDay = conferenceDays[currentDayIndex];
    const isNoDate = currentDayIndex === -1;

    return {
        conferenceDays,
        currentDay,
        currentDayIndex,
        currentDateLabel: isNoDate ? t('noDateLectures') : (currentDay?.label || ''),
        onPrevDay: () => setCurrentDayIndex(Math.max(0, currentDayIndex - 1)),
        onNextDay: () => setCurrentDayIndex(isNoDate ? 0 : Math.min(conferenceDays.length - 1, currentDayIndex + 1)),
        onDaySelect: (i: number) => setCurrentDayIndex(Math.max(-1, Math.min(conferenceDays.length - 1, i))),
        disablePrev: currentDayIndex <= 0,
        disableNext: !isNoDate && currentDayIndex === conferenceDays.length - 1,
    };
}
